import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ShareModal from "@/src/components/action-modals/share-modal";
import GlobalNewsCard from "@/src/components/news/global-news-cards";
import { NewsDetailContentSkeleton } from "@/src/components/news/loading-skeleton/news-detail-skeleton";
import ContentRenderer from "@/src/components/text-editor/content-renderer";
import AttachmentViewer from "@/src/components/ui-kit/attachment-viewer";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import DropDown from "@/src/components/ui-kit/drop-down";
import { formatArticleDate } from "@/src/hooks/features/news/use-latest-news";
import { useWikiArticleDetail } from "@/src/hooks/features/wiki/use-wiki-detail";
import { DEFAULT_AUTHOR_AVATAR } from "@/src/services/wiki/wiki.hooks";
import { router } from "expo-router";
import {
  Image,
  Platform,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

const isWeb = Platform.OS === "web";

function ArticleTag({ label }: { label: string }) {
  return (
    <View className="flex-row items-center px-2 py-1 rounded-full bg-primary/10">
      <Text className="text-xs font-medium text-primary">{label}</Text>
    </View>
  );
}

export default function WikiDetailScreen() {
  const {
    t,
    article,
    isLoading,
    isError,
    primaryPalette,
    colors,
    resolvedScheme,
    categoryNames,
    attachments,
    dropdownItems,
    resolveAuthorName,
    globalNewsData,
    isLoadingGlobalNews,
    isGlobalNewsError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showShareModal,
    setShowShareModal,
    deleteArticle,
  } = useWikiArticleDetail();

  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const renderContent = () => {
    if (isLoading) return <NewsDetailContentSkeleton />;

    if (isError || !article) {
      return (
        <View className="flex-1 items-center justify-center">
          <Image
            source={require("@/src/assets/images/error.png")}
            style={{ width: 100, height: 100 }}
            resizeMode="contain"
          />
          <Text className="text-slate text-sm">
            {t("LABEL_ARTICLE_NOT_FOUND")}
          </Text>
        </View>
      );
    }

    return (
      <>
        {isWeb && (
          <View className="flex-row items-center border border-ash-3 bg-chalk px-4 py-6 rounded-2xl">
            <BackNavigationButton
              label={t("LABEL_BACK")}
              iconSize={16}
              iconClassName="text-onyx"
              labelClassName="text-onyx"
              containerStyle={{ marginBottom: 0 }}
              onPress={() => router.back()}
            />
            <Text className="flex-1 text-center text-base font-semibold text-onyx">
              {t("LABEL_ARTICLE_DETAIL")}
            </Text>
            <View style={{ width: 60 }} />
          </View>
        )}

        <View className="flex-1 flex-col border border-ash-3 bg-chalk rounded-2xl p-4">
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {!isWeb && (
              <View className="flex-row items-center mb-4">
                <BackNavigationButton
                  iconClassName="text-onyx"
                  containerStyle={{ marginBottom: 0 }}
                  onPress={() => router.back()}
                />
                <Text className="flex-1 text-center text-base font-semibold text-onyx">
                  {t("LABEL_ARTICLE_DETAIL")}
                </Text>
                <View style={{ width: 60 }} />
              </View>
            )}

            <View className="flex-row items-start justify-between gap-4 mt-2">
              <View className="flex-1 gap-2">
                <Text className="text-2xl font-bold text-onyx">
                  {article.Title}
                </Text>

                {categoryNames.length > 0 && (
                  <View className="flex-row flex-wrap gap-2">
                    {categoryNames.map((name) => (
                      <View
                        key={name}
                        className="bg-primary-2 rounded-full px-2 py-1"
                      >
                        <ArticleTag label={name} />
                      </View>
                    ))}
                  </View>
                )}

                <View className="flex-row items-center gap-2 mt-1">
                  <Image
                    source={
                      article.authorSnapshot?.profileImageUrl
                        ? { uri: article.authorSnapshot.profileImageUrl }
                        : DEFAULT_AUTHOR_AVATAR
                    }
                    style={{ width: 28, height: 28, borderRadius: 14 }}
                  />
                  <Text className="text-sm font-medium text-onyx">
                    {resolveAuthorName(article)}
                  </Text>
                  <Text className="text-xs text-slate">
                    {formatArticleDate(article.CreatedDate)}
                  </Text>
                </View>
              </View>

              <DropDown items={dropdownItems} />
            </View>

            {article.coverImageUrl && (
              <View className="rounded-2xl overflow-hidden mt-4">
                <Image
                  source={{ uri: article.coverImageUrl }}
                  style={{ width: "100%", height: isWeb ? 400 : 200 }}
                  resizeMode="cover"
                />
              </View>
            )}

            <View className="mt-4 min-h-[100px]">
              <ContentRenderer
                key={resolvedScheme}
                content={article.Content ?? ""}
                primaryColor={primaryPalette.default}
                textColor={colors.onyxDefault}
                borderColor={colors.ash3}
                containerBg={colors.chalkDefault}
              />
            </View>

            {attachments.length > 0 && (
              <View className="border-t border-ash-3 pt-4 gap-3 mt-4">
                <Text className="text-sm font-semibold text-onyx">
                  {t("LABEL_ATTACHMENT")}
                </Text>
                <AttachmentViewer attachments={attachments} />
              </View>
            )}
          </ScrollView>
        </View>
      </>
    );
  };

  return (
    <View className="flex-row flex-1 gap-4 bg-obsidian">
      <View className="flex-col flex-1 gap-4">{renderContent()}</View>

      {!isMobile && (
        <GlobalNewsCard
          data={globalNewsData}
          isLoading={isLoadingGlobalNews}
          isError={isGlobalNewsError}
        />
      )}

      {article && (
        <>
          <ConfirmModal
            visible={showDeleteConfirm}
            title="BTN_DELETE"
            description="LABEL_DESC_DELETE_WIKI"
            confirmText={t("BTN_DELETE")}
            cancelText={t("BTN_CANCEL")}
            onConfirm={() =>
              deleteArticle({ itemId: article.ItemId, entity: article })
            }
            onCancel={() => setShowDeleteConfirm(false)}
          />

          <ShareModal
            visible={showShareModal}
            onClose={() => setShowShareModal(false)}
            resourceId={article.ItemId}
            resource="wiki"
            title={article.Title}
          />
        </>
      )}
    </View>
  );
}
